import React, {useEffect, useState} from 'react'
import {Modal, Form, Button, Card, Alert, Spinner, Row, Col} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {useIntl} from 'react-intl'
import {
  Channel,
  CHANNEL_TYPE_OPTIONS,
  ChannelProvider,
  ChannelType,
} from '../../../types/assignmentGroups'
import {MultiSelectWithActions} from '../../../components/MultiSelectWithActions'
import {TEST_TWILIO_CREDENTIALS} from '../../../gql/mutations/channelMutations'
import {GROUP_MESSAGES as M, channelTypeLabel} from '../assignmentGroupMessages'
import {TELEPHONY_MESSAGES as TM} from '../../../helpers/telephonyMessages'

const PROVIDER_LABELS: Record<ChannelProvider, string> = {
  TWILIO: 'Twilio',
  BOTMAKER: 'Botmaker',
  META: 'Meta Cloud API',
}

interface ChannelFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: Partial<Channel>) => Promise<Channel>
  saving: boolean
  initialData?: Channel | null
  groupMembers: Array<{value: string; label: string}>
  /** Providers selectable from the Provider dropdown. Defaults to Twilio only (Assignment Groups today only support Twilio). */
  availableProviders?: ChannelProvider[]
  /** Channel types to hide from the Channel Type dropdown when creating a new channel (e.g. types the owner already has one of). Ignored while editing. */
  excludeChannelTypes?: ChannelType[]
}

export const ChannelFormModal: React.FC<ChannelFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  saving,
  initialData,
  groupMembers,
  availableProviders = ['TWILIO'],
  excludeChannelTypes = [],
}) => {
  const intl = useIntl()
  const [testTwilioCredentialsMutation, {loading: testingCredentialsLoading}] =
    useMutation(TEST_TWILIO_CREDENTIALS)

  const allowedTypesForProvider = (p: ChannelProvider) =>
    initialData
      ? CHANNEL_TYPE_OPTIONS[p]
      : CHANNEL_TYPE_OPTIONS[p].filter((t) => !excludeChannelTypes.includes(t))

  const [provider, setProvider] = useState<ChannelProvider>(availableProviders[0])
  const [channelType, setChannelType] = useState<ChannelType>('SMS')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [assignedMemberIds, setAssignedMemberIds] = useState<string[]>([])
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [savedChannelId, setSavedChannelId] = useState<number | null>(null)
  const [webhookToken, setWebhookToken] = useState<string | null>(null)
  const [testMessage, setTestMessage] = useState<{type: 'success' | 'error'; text: string} | null>(
    null
  )

  // Initialize from initialData or reset
  useEffect(() => {
    if (initialData) {
      setProvider(initialData.provider || availableProviders[0])
      setPhoneNumber(initialData.phone_number)
      setIsActive(initialData.is_active)
      setChannelType(
        initialData.channel_type ||
          CHANNEL_TYPE_OPTIONS[initialData.provider || availableProviders[0]][0]
      )
      setAssignedMemberIds(
        (initialData.assigned_member_ids || [])
          .filter((id) => id != null)
          .map((id) => id.toString())
      )

      const token =
        initialData.webhook_token ||
        (initialData as any).webhookToken ||
        ''
      setWebhookToken(token || null)

      let parsedCreds = initialData.credentials || {}
      if (typeof parsedCreds === 'string') {
        try {
          parsedCreds = JSON.parse(parsedCreds)
        } catch (e) {
          parsedCreds = {}
        }
      }
      // Ensure webhook_token is in credentials
      if (token && typeof parsedCreds === 'object') {
        parsedCreds = {...parsedCreds, webhook_token: token}
      }
      setCredentials(parsedCreds)
      setSavedChannelId(initialData.id)
    } else {
      const firstProvider =
        availableProviders.find((p) => allowedTypesForProvider(p).length > 0) ||
        availableProviders[0]
      setProvider(firstProvider)
      setPhoneNumber('')
      setIsActive(true)
      setChannelType(allowedTypesForProvider(firstProvider)[0])
      setAssignedMemberIds([])
      setCredentials({})
      setWebhookToken(null)
      setSavedChannelId(null)
    }
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isOpen])

  const handleProviderChange = (nextProvider: ChannelProvider) => {
    setProvider(nextProvider)
    const allowedTypes = allowedTypesForProvider(nextProvider)
    if (!allowedTypes.includes(channelType)) {
      setChannelType(allowedTypes[0])
    }
  }

  const handleSave = async () => {
    setError(null)

    if (provider === 'TWILIO') {
      const requiredFields = ['account_sid', 'auth_token']
      const missingFields = requiredFields.filter((field: string) => !credentials[field])
      if (missingFields.length > 0) {
        setError(intl.formatMessage(M.channelMissingRequired, {fields: missingFields.join(', ')}))
        return
      }
    } else if (!phoneNumber.trim()) {
      setError('Phone number / line ID is required')
      return
    }

    try {
      const data: Partial<Channel> = {
        id: savedChannelId || undefined,
        provider,
        channel_type: channelType,
        phone_number: phoneNumber,
        is_active: isActive,
        assigned_member_ids: assignedMemberIds.map((id) => parseInt(id, 10)),
        credentials,
      }

      const savedChannel = await onSave(data)
      if (savedChannel && savedChannel.id) {
        setSavedChannelId(savedChannel.id)
        const token =
          savedChannel.webhook_token ||
          (savedChannel as any).webhookToken ||
          ''
        if (token) {
          setWebhookToken(token)
        }

        let parsedCreds = savedChannel.credentials || {}
        if (typeof parsedCreds === 'string') {
          try {
            parsedCreds = JSON.parse(parsedCreds)
          } catch (e) {
            parsedCreds = {}
          }
        }
        // Ensure webhook_token is in credentials
        if (token && typeof parsedCreds === 'object') {
          parsedCreds = {...parsedCreds, webhook_token: token}
        }
        setCredentials(parsedCreds)
      }
    } catch (err: any) {
      const errorMessage =
        err?.message || err?.response?.data?.error || intl.formatMessage(TM.errorSavingChannel)
      setError(errorMessage)
      console.error('Save channel error:', err)
    }
  }

  const handleCredentialChange = (field: string, value: string) => {
    setCredentials((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleTestCredentials = async () => {
    setTestMessage(null)
    const missingFields = ['account_sid', 'auth_token'].filter((field) => !credentials[field])

    if (missingFields.length > 0) {
      setTestMessage({
        type: 'error',
        text: intl.formatMessage(M.channelMissingFields, {fields: missingFields.join(', ')}),
      })
      return
    }

    try {
      const result = await testTwilioCredentialsMutation({
        variables: {
          accountSid: credentials.account_sid,
          authToken: credentials.auth_token,
        },
      })

      if (result.data?.testTwilioCredentials?.success) {
        setTestMessage({
          type: 'success',
          text: intl.formatMessage(M.channelTestOk, {
            account: result.data.testTwilioCredentials.accountName,
          }),
        })
      } else {
        setTestMessage({
          type: 'error',
          text:
           
            result.data?.testTwilioCredentials?.error ||
            intl.formatMessage(M.channelTestFailed),
        })
      }
    } catch (err: any) {
      const errorMessage =
        err.message || intl.formatMessage(M.channelTestFailed)
      setTestMessage({
        type: 'error',
        text: errorMessage,
      })
      console.error(err)
    }
  }

  const getWebhookUrl = () => {
    if (!savedChannelId) return null
    const token =
      webhookToken ||
      credentials.webhook_token ||
      initialData?.webhook_token ||
      (initialData as any)?.webhookToken ||
      '[token]'
    if (provider === 'TWILIO') {
      return `https://app.policytools.com/webhooks/twilio/${channelType.toLowerCase()}/${token}/`
    }
    if (provider === 'BOTMAKER') {
      return `https://app.policytools.com/webhooks/botmaker/${token}/`
    }
    if (provider === 'META') {
      return `https://app.policytools.com/webhooks/meta/${token}/`
    }
    return null
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(
          intl.formatMessage(
            {
              id: 'telephony.assignmentGroups.toast.copiedToClipboard',
              defaultMessage: '{label} copied to clipboard!',
            },
            {label}
          )
        )
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({
            id: 'telephony.assignmentGroups.toast.copyFailed',
            defaultMessage: 'Failed to copy to clipboard',
          })
        )
      })
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='lg' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage(savedChannelId ? M.channelEditTitle : M.channelAddTitle)}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant='danger' className='mb-3'>
            {error}
          </Alert>
        )}

        <Form>
          <Row className='g-3 mb-3'>
            {availableProviders.length > 1 && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label className='fw-semibold'>
                    {intl.formatMessage(M.colProvider)}
                  </Form.Label>
                  <Form.Select
                    value={provider}
                    onChange={(e) => handleProviderChange(e.target.value as ChannelProvider)}
                    size='sm'
                  >
                    {availableProviders.map((p) => (
                      <option key={p} value={p}>
                        {PROVIDER_LABELS[p]}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}

            <Col md={availableProviders.length > 1 ? 4 : 6}>
              <Form.Group>
                <Form.Label className='fw-semibold'>
                  {intl.formatMessage(M.channelType)}
                </Form.Label>
                <Form.Select
                  value={channelType}
                  onChange={(e) => setChannelType(e.target.value as ChannelType)}
                  size='sm'
                >
                  {allowedTypesForProvider(provider).map((type) => (
                    <option key={type} value={type}>
                      {channelTypeLabel(type)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={availableProviders.length > 1 ? 4 : 6}>
              <Form.Group>
                <Form.Label className='fw-semibold'>
                  {intl.formatMessage(
                    provider === 'TWILIO'
                      ? M.channelPhoneNumber
                      : provider === 'BOTMAKER'
                      ? M.channelBotmakerLine
                      : M.channelMetaPhoneId
                  )}
                </Form.Label>
                <Form.Control
                  type='text'
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={
                    provider === 'TWILIO'
                      ? '+1234567890'
                      : provider === 'BOTMAKER'
                      ? 'Line number'
                      : '1234567890'
                  }
                  size='sm'
                />
              </Form.Group>
            </Col>
          </Row>

          {provider === 'TWILIO' ? (
            <Form.Group className='mb-3'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <Form.Label className='fw-semibold mb-0'>
                  {intl.formatMessage(M.channelCredentials)}
                </Form.Label>
                <Form.Switch
                  id='isActive'
                  label={intl.formatMessage(M.statusActive)}
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className='fw-semibold'
                />
              </div>
              <Card className='bg-light p-3'>
                <Row className='g-3 mb-3'>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label className='fw-semibold' style={{fontSize: '0.875rem'}}>
                        {intl.formatMessage(M.channelAccountSid)}
                      </Form.Label>
                      <Form.Control
                        type='text'
                        value={credentials.account_sid || ''}
                        onChange={(e) => handleCredentialChange('account_sid', e.target.value)}
                        placeholder={intl.formatMessage(M.channelAccountSidPlaceholder)}
                        size='sm'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label className='fw-semibold' style={{fontSize: '0.875rem'}}>
                        {intl.formatMessage(M.channelAuthToken)}
                      </Form.Label>
                      <Form.Control
                        type='password'
                        value={credentials.auth_token || ''}
                        onChange={(e) => handleCredentialChange('auth_token', e.target.value)}
                        placeholder={intl.formatMessage(M.channelAuthTokenPlaceholder)}
                        size='sm'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className='d-flex align-items-end'>
                    <Button
                      variant='outline-secondary'
                      size='sm'
                      onClick={handleTestCredentials}
                      disabled={testingCredentialsLoading}
                      className='w-100'
                    >
                      {testingCredentialsLoading ? (
                        <>
                          <Spinner as='span' animation='border' size='sm' />
                        </>
                      ) : (
                        intl.formatMessage(TM.test)
                      )}
                    </Button>
                  </Col>
                </Row>
                {testMessage && (
                  <Alert
                    variant={testMessage.type === 'success' ? 'success' : 'danger'}
                    className='mb-0'
                  >
                    {testMessage.text}
                  </Alert>
                )}
              </Card>
            </Form.Group>
          ) : (
            <Form.Group className='mb-3'>
              <Form.Switch
                id='isActive'
                label='Active'
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className='fw-semibold'
              />
            </Form.Group>
          )}

          {groupMembers.length > 0 && (
            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold'>
                {intl.formatMessage(M.colAssignedMembers)}
              </Form.Label>
              <Form.Text className='d-block text-muted mb-2'>
                {intl.formatMessage(M.channelAssignedMembersHint)}
              </Form.Text>
              <MultiSelectWithActions
                value={groupMembers.filter((opt) => assignedMemberIds.includes(opt.value))}
                onChange={(selected: any) => {
                  const values = Array.isArray(selected)
                    ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
                    : []
                  setAssignedMemberIds(values)
                }}
                options={groupMembers}
                placeholder={intl.formatMessage(M.channelSelectMembers)}
              />
            </Form.Group>
          )}

          {savedChannelId && getWebhookUrl() && (
            <>
              <Card className='bg-info-light p-3 mb-3'>
                <Form.Group>
                  <Form.Label className='fw-semibold mb-2'>
                    {intl.formatMessage(M.channelWebhookUrlLabel)}
                  </Form.Label>
                  <div className='d-flex gap-2'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={getWebhookUrl() || ''}
                      className='font-monospace'
                      style={{fontSize: '0.875rem'}}
                      size='sm'
                    />
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() =>
                        copyToClipboard(
                          getWebhookUrl() || '',
                          intl.formatMessage(M.channelWebhookUrl)
                        )
                      }
                    >
                      <i className='bi bi-files' /> {intl.formatMessage(M.copy)}
                    </Button>
                  </div>
                  <Form.Text className='d-block mt-2 text-muted'>
                    {intl.formatMessage(M.channelWebhookHint, {provider})}
                  </Form.Text>
                </Form.Group>
              </Card>

              <Card className='bg-warning-light p-3 mb-3'>
                <Form.Group>
                  <Form.Label className='fw-semibold mb-2'>
                    {intl.formatMessage(M.channelStatusUrlLabel)}
                  </Form.Label>
                  <div className='d-flex gap-2'>
                    <Form.Control
                      type='text'
                      readOnly
                      value='https://app.policytools.com/webhooks/twilio/status/'
                      className='font-monospace'
                      style={{fontSize: '0.875rem'}}
                      size='sm'
                    />
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() =>
                        copyToClipboard(
                          'https://app.policytools.com/webhooks/twilio/status/',
                          'Status Callback URL'
                        )
                      }
                    >
                      <i className='bi bi-files' /> {intl.formatMessage(M.copy)}
                    </Button>
                  </div>
                  <Form.Text className='d-block mt-2 text-muted'>
                    {intl.formatMessage(M.channelStatusHint)}
                  </Form.Text>
                </Form.Group>
              </Card>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner as='span' animation='border' size='sm' className='me-2' />
              {intl.formatMessage(M.saving)}
            </>
          ) : (
            intl.formatMessage(M.channelSave)
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
